import type { IsoDate } from './calendar.js';
import { currentIsoWeek, FACHLICHE_ZEITZONE, mondayOf } from './iso-week.js';
import type { IsoWeek } from './iso-week.js';
import type { ProjectCreateStatus, ProjectStatus } from './project-fields.js';

/**
 * Projektstatus und seine Wirkung auf die Auslastung (AC-202) — die EINE Stelle,
 * an der beides bestimmt wird.
 *
 * Bisher war die Reihenfolge der Excel-Blaetter Rechenlogik: Akquise-Blaetter
 * vor „Maske1" zaehlten nicht. Hier ersetzt sie ein Status mit Historie. Wer
 * kuenftig Belegungen bewertet — die Auslastung mit Fachgruppensummen (AC-407),
 * die getrennte Akquise-Ausweisung (AC-210), die serverseitige Aggregation
 * (AC-410) —, fragt `belastungInWoche` und rechnet nicht daneben.
 *
 * Ein Statuswechsel wirkt ab dem Montag der Woche, in der er erfolgt
 * (`wirksamAb`), nie rueckwirkend (AK 3). Gespeichert sind nur die WECHSEL
 * (`project_status_changes`); ohne Wechsel gilt der aktuelle Status fuer jede
 * Woche, vor dem ersten Wechsel dessen Ausgangsstatus. Deshalb braucht ein
 * Bestandsprojekt keinen Nachtrag (Entscheidung E4).
 *
 * Der Status gehoert zum ASAS-Projekt, nicht zu einem PACS-Datensatz (J3):
 * Belegungen auf 2401 und 2401W tragen dieselbe `projectId`.
 *
 * Transportfrei (ADR-04) und OHNE Zod: die Oberflaeche laedt die Datei ueber
 * `@asas/domain/project-status`.
 */

/** Wie die Belegungen eines Projekts in einer Woche zaehlen (E5). */
export const BELASTUNGEN = ['verbindlich', 'akquise', 'keine'] as const;
export type Belastung = (typeof BELASTUNGEN)[number];

/**
 * Wirkung je Status (E5). `aktiv` heisst fachlich „beauftragt" (E1). Die
 * spaetere SQL-Aggregation (AC-410) bekommt diese Zuordnung von hier.
 */
export const STATUS_WIRKUNG: Readonly<Record<ProjectStatus, Belastung>> = {
  akquise: 'akquise',
  aktiv: 'verbindlich',
  abgeschlossen: 'keine',
  archiviert: 'keine',
  verloren: 'keine',
};

/** Anzeige der Statuswerte; `aktiv` heisst in der Oberflaeche „Beauftragt" (E1). */
export const STATUS_BEZEICHNUNG: Readonly<Record<ProjectStatus, string>> = {
  akquise: 'Akquise',
  aktiv: 'Beauftragt',
  abgeschlossen: 'Abgeschlossen',
  archiviert: 'Archiviert',
  verloren: 'Verloren',
};

/** Standardfilter der Arbeits- und Planungslisten (E6, AK 4). */
export const ARBEITSLISTE_STATUSES = [
  'akquise',
  'aktiv',
] as const satisfies readonly ProjectStatus[];

/**
 * Erlaubte Wechsel (E3). Die Wiederaufnahme `abgeschlossen → aktiv` gehoert zu
 * AC-208 (eigenes Recht, Pflichtbegruendung), `→ archiviert` ebenfalls.
 */
export const STATUS_UEBERGAENGE: Readonly<Record<ProjectStatus, readonly ProjectStatus[]>> = {
  akquise: ['aktiv', 'verloren'],
  aktiv: ['abgeschlossen'],
  abgeschlossen: [],
  archiviert: [],
  verloren: ['akquise'],
};

export function isErlaubterUebergang(von: ProjectStatus, nach: ProjectStatus): boolean {
  return STATUS_UEBERGAENGE[von].includes(nach);
}

/**
 * Verlangt die Anlage mit diesem Status das Recht `projects:change_status`
 * (E7)? Nur `akquise` erzeugt keine verbindliche Last und keine Referenz.
 */
export function anlageVerlangtStatusRecht(status: ProjectCreateStatus): boolean {
  return status !== 'akquise';
}

/**
 * Ob in einem Projekt Arbeitspakete angelegt werden duerfen — aus der
 * Bibliothek oder freihaendig (AC-303, E7):
 *
 *   ja               mit `projects:manage`
 *   mit_statusrecht  zusaetzlich `projects:change_status` — ein
 *                    abgeschlossenes Projekt ist Referenz (AC-202 E7), und wer
 *                    es nachtraeglich ergaenzt, aendert sie
 *   nein             ein verlorenes oder archiviertes Projekt bekommt keine
 *                    Arbeitspakete
 *
 * Die Story „Abgeschlossene Projekte als Referenz erfassen" (J3) aendert die
 * Regel HIER.
 */
export const INSTANZIIERUNG_JE_STATUS: Readonly<
  Record<ProjectStatus, 'ja' | 'mit_statusrecht' | 'nein'>
> = {
  akquise: 'ja',
  aktiv: 'ja',
  abgeschlossen: 'mit_statusrecht',
  verloren: 'nein',
  archiviert: 'nein',
};

/** Die Pruefung zu `INSTANZIIERUNG_JE_STATUS`; `hatStatusRecht` heisst `projects:change_status`. */
export function instanziierenErlaubt(status: ProjectStatus, hatStatusRecht: boolean): boolean {
  const regel = INSTANZIIERUNG_JE_STATUS[status];
  return regel === 'ja' || (regel === 'mit_statusrecht' && hatStatusRecht);
}

/**
 * Nach welchem Wechsel der Dienst die künftigen Belegungen meldet (AC-202 E5,
 * umgesetzt in AC-401 E11): nach `abgeschlossen` und `verloren`. Gelöscht wird
 * nichts; die Meldung sagt nur, wie viele Belegungen ab der Wirksamkeitswoche
 * stehen bleiben.
 */
export function meldetKuenftigeBelegungen(nach: ProjectStatus): boolean {
  return nach === 'abgeschlossen' || nach === 'verloren';
}

/**
 * Ein gespeicherter Statuswechsel. `effectiveFrom` ist ein Montag;
 * `createdAt` ordnet mehrere Wechsel derselben Woche und ist ein UTC-Zeitstempel
 * mit fester Stellenzahl (`YYYY-MM-DDTHH:MM:SS.ffffffZ`), damit der
 * Textvergleich die Zeitfolge ist.
 */
export interface StatusWechsel {
  readonly effectiveFrom: IsoDate;
  readonly statusFrom: ProjectStatus;
  readonly statusTo: ProjectStatus;
  readonly createdAt: string;
}

/** Ab welchem Montag ein Wechsel zum Zeitpunkt `now` wirkt: der laufenden ISO-Woche. */
export function wirksamAb(now: Date): IsoDate {
  return mondayOf(currentIsoWeek(now, FACHLICHE_ZEITZONE));
}

function frueher(a: StatusWechsel, b: StatusWechsel): boolean {
  return a.effectiveFrom === b.effectiveFrom
    ? a.createdAt < b.createdAt
    : a.effectiveFrom < b.effectiveFrom;
}

/**
 * Der Status, der in dieser Woche galt: der Zielstatus des juengsten Wechsels,
 * der spaetestens an ihrem Montag wirkt; vor dem ersten Wechsel dessen
 * Ausgangsstatus; ohne Wechsel `aktuell`. Die Reihenfolge der Eingabe spielt
 * keine Rolle.
 */
export function statusInWoche(
  aktuell: ProjectStatus,
  wechsel: readonly StatusWechsel[],
  week: IsoWeek,
): ProjectStatus {
  const montag = mondayOf(week);
  let erster: StatusWechsel | null = null;
  let gilt: StatusWechsel | null = null;

  for (const eintrag of wechsel) {
    if (erster === null || frueher(eintrag, erster)) erster = eintrag;
    if (eintrag.effectiveFrom <= montag && (gilt === null || frueher(gilt, eintrag))) {
      gilt = eintrag;
    }
  }

  if (gilt !== null) return gilt.statusTo;
  return erster === null ? aktuell : erster.statusFrom;
}

/** Wie die Belegungen des Projekts in dieser Woche zaehlen (AK 1, AK 2, AK 3). */
export function belastungInWoche(
  aktuell: ProjectStatus,
  wechsel: readonly StatusWechsel[],
  week: IsoWeek,
): Belastung {
  return STATUS_WIRKUNG[statusInWoche(aktuell, wechsel, week)];
}
